import * as React from 'react';
import { FlexDiv } from '../../products/style';
import moment from 'moment';
import { Translate, translateToString } from '../../../styles/global/translate';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import Popup from '../../../components_v2/popup/Popup';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { BoolCheckbox } from '../../forms/PopupField';
import { RedSidely } from '../../../styles/global/css/Utils';
import trash from '../../../images/ui_icon/trash_black.svg';
import add from '../../../images/icon/add_2.png';
import styled from 'styled-components';
import Add from '../../../components_v2/add/Add';
import _ from 'lodash';
import { DefaultInput } from '../../../styles/global/css/GlobalInput';

const daysOfWeek = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
] as const;
type DayOfWeek = (typeof daysOfWeek)[number];
export type HoursJson = {
	[day in DayOfWeek]?: {
		start: number;
		end: number;
	}[];
};

const minutesToTime = (minutes: number): string => {
	return moment.utc(minutes * 60 * 1000).format('HH:mm');
};

const StyledTimeInput = styled.input`
	${DefaultInput}
	width: 80px;
	padding: 0 5px;
	&:invalid {
		border-color: ${RedSidely};
	}
`;

function TimeInput(props: { value: string, hours: HoursJson, setHours: React.Dispatch<React.SetStateAction<HoursJson>>, day: DayOfWeek, i: number, start: boolean, min?: string, max?: string }) {
	const { value, hours, setHours, day, i, start } = props;

	return (
		<StyledTimeInput
			id={`${day}-${i}-${props.start ? 'start' : 'end'}`}
			name={`${day}-${i}-${props.start ? 'start' : 'end'}`}
			min={props.min ?? '00:00'}
			max={props.max ?? '23:59'}
			placeholder='HH:mm'
			type='time'
			value={value}
			onChange={(e) => {
				const newHours = { ...hours };
				if (!newHours[day]) newHours[day] = [];
				if (start)
					newHours[day][i] = {
						...newHours[day][i],
						start: moment(e.target.value, 'HH:mm').diff(moment().startOf('day'), 'minutes'),
					};
				else
					newHours[day][i] = {
						...newHours[day][i],
						end: moment(e.target.value, 'HH:mm').diff(moment().startOf('day'), 'minutes'),
					};
				setHours(newHours);
			}}
		/>
	);
}

export default function HoursDisplayer(props: { hours_json: HoursJson, onChange?: (hours: HoursJson) => void, tableMode?: boolean }): JSX.Element {
	const { hours_json, onChange, tableMode } = props;
	const [editOpen, setEditOpen] = React.useState(false);
	const [hours, setHours] = React.useState<HoursJson>(hours_json);
	const [reset, setReset] = React.useState(false);
	const [isValid, setIsValid] = React.useState(true);

	React.useEffect(() => {
		if (reset) {
			setHours(hours_json);
			setReset(false);
		}
	}, [reset, hours_json]);

	React.useEffect(() => {
		if (editOpen) {
			setHours(hours_json);
			setIsValid(true);
		}
	}, [editOpen, hours_json]);

	const validateHours = () => {
		for (const day of daysOfWeek) {
			const dayHours = hours[day];
			if (dayHours) {
				for (let i = 0; i < dayHours.length; i++) {
					const start = dayHours[i].start;
					const end = dayHours[i].end;

					if (start >= end || start < 0 || end > 1439) {
						setIsValid(false);
						return;
					}

					if (i > 0 && start <= dayHours[i - 1].end) {
						setIsValid(false);
						return;
					}
					if (i < dayHours.length - 1 && end >= dayHours[i + 1].start) {
						setIsValid(false);
						return;
					}
				}
			}
		}
		setIsValid(true);
	};

	React.useEffect(() => {
		validateHours();
	}, [hours]);

	return (
		<>
			<Popup isOpen={editOpen} popupMode={PopupMode.Centered} disableEsc={true} popupStyle={{ width: '400px', height: '550px' }}>
				<FlexDiv flow='column' align='stretch' width='100%' height='550px' justify='space-between' padding='15px 15px 30px 15px' overflow='auto'>
					<FlexDiv flow='column' align='stretch' width='100%' gap='8px'>
						{
							daysOfWeek.map((day) => {
								const value = hours[day];
								return (
									<FlexDiv key={`additionnal-hours-${day}`} justify='space-between' width='100%'>
										<FlexDiv flow='column' width='100px' gap='4px' align='start' alignSelf='flex-start'>
											<div style={{ alignSelf: 'flex-start' }}>{translateToString(`days.${day}`)}</div>
											<FlexDiv gap='4px'>
												<BoolCheckbox type='checkbox' checked={!(value && value.length > 0)} onChange={() => {
													if (value && value.length > 0) {
														const newHours = { ...hours };
														delete newHours[day];
														setHours(newHours);
													} else {
														setHours({
															...hours,
															[day]: [{ start: 540, end: 1140 }],
														});
													}
												}} />
												<Translate id='days.closed' />
											</FlexDiv>
										</FlexDiv>
										<div>
											{value && value.length > 0 &&
												<FlexDiv gap='6px' flow='column' align='start' width='200px'>
													{value.map((v, i) => {
														const newHours = { ...hours };
														return <FlexDiv gap='5px' key={`${day}-${i}`} >
															<TimeInput
																value={minutesToTime(v.start)}
																hours={hours}
																min={i > 0 && newHours[day] ? minutesToTime(newHours[day][i - 1].end) : undefined}
																max={newHours[day] ? minutesToTime(newHours[day][i].end) : undefined}
																setHours={setHours}
																day={day}
																i={i}
																start={true}
															/>
															<span>-</span>
															<TimeInput
																value={minutesToTime(v.end)}
																hours={hours}
																setHours={setHours}
																min={newHours[day] ? minutesToTime(newHours[day][i].start) : undefined}
																max={newHours[day] && i < newHours[day].length - 1 ? minutesToTime(newHours[day][i + 1].start) : undefined}
																day={day}
																i={i}
																start={false}
															/>
															{hours[day] && (i == 0 || i !== hours[day].length - 1) &&
																<img src={add} alt='add' style={{ cursor: 'pointer', width: '13px', aspectRatio: '1', marginLeft: '2px' }} onClick={() => {
																	const newHours = { ...hours };
																	if (!newHours[day]) newHours[day] = [];
																	const lastClosing = newHours[day][newHours[day].length - 1].end;
																	const newStart = lastClosing + 60 > 1439 ? 1439 : lastClosing + 60;
																	const newEnd = newStart + 60 > 1439 ? 1439 : newStart + 60;
																	newHours[day].push({ start: newStart, end: newEnd });
																	setHours(newHours);
																}} />
															}
															{hours[day] && i !== 0 && i === hours[day].length - 1 &&
																<img src={trash} alt='delete' style={{ cursor: 'pointer', width: '18px', aspectRatio: '1' }} onClick={() => {
																	if (newHours[day]) {
																		newHours[day] = newHours[day].filter((_, index) => index !== i);
																		if (newHours[day].length === 0) {
																			delete newHours[day];
																		}
																		setHours(newHours);
																	}
																}} />
															}
														</FlexDiv>;
													})}
												</FlexDiv>
											}
										</div>
									</FlexDiv>
								);
							})
						}
					</FlexDiv>
					<FlexDiv justify='center'>
						<DefaultButton width='130px' buttonStyle={ButtonStyle.White} onClick={() => {
							setReset(true);
							setEditOpen(false);
						}}>
							<Translate id='cancel' />
						</DefaultButton>
						<DefaultButton disabled={!isValid} width='130px' onClick={() => {
							if (onChange) {
								onChange(hours);
							}
							setEditOpen(false);
						}}
						>
							<Translate id='save' />
						</DefaultButton>
					</FlexDiv>
				</FlexDiv>
			</Popup>
			<FlexDiv flow='column' align='stretch' gap='5px' fontSize={tableMode ? '12px' : '13px'} height={tableMode ? '40px' : 'auto'} overflow={tableMode ? 'auto' : undefined} overflowX='hidden'>
				{_.isEmpty(hours_json) ?
					<div>-</div>
					:
					daysOfWeek.map((day) => {
						const value = hours_json[day];
						return (
							<FlexDiv key={`additionnal-hours-${day}`} justify='space-between' gap={tableMode ? '4px' : undefined}>
								<div style={{ alignSelf: 'flex-start' }}>{translateToString(`days.${day}`)}</div>
								<div>
									{value && value.length > 0 ? (
										value.map((v, i) => (
											<FlexDiv key={`${day}-${i}`} flow='column' width='100px' gap='5px' align='start'>
												<div>{minutesToTime(v.start)}-{minutesToTime(v.end)}</div>
											</FlexDiv>
										))
									) : (
										<FlexDiv flow='column' width='100px' gap='5px' align='start'>{translateToString('days.closed')}</FlexDiv>
									)}
								</div>
							</FlexDiv>
						);
					})
				}
				{props.onChange &&
					<Add size={'22px'} onClick={() => setEditOpen(true)} />
				}
			</FlexDiv>
		</>
	);
}
